class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    this.activeTrigger = null;
    this.renderRequestId = 0;
    this.refreshPromise = null;
    this.debug = window.BuddyCartDrawerDebug === true;

    if (this.drawer) {
      this.drawer.addEventListener('close', this.onDrawerClose.bind(this));
    }

    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    const renderRequestId = ++this.renderRequestId;

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        if (renderRequestId !== this.renderRequestId) return;

        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');
          
          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open(trigger = null) {
    if (!this.drawer) {
      this.log('drawer missing; falling back to cart page');
      window.location.href = window.routes.cart_url;
      return;
    }

    this.prepareForOpen();

    const detailsElement = this.drawer.querySelector('details');
    const summaryElement = detailsElement && detailsElement.querySelector('summary');

    if (!detailsElement) {
      this.log('drawer details element missing; falling back to cart page');
      window.location.href = window.routes.cart_url;
      return;
    }
    this.activeTrigger = trigger || document.activeElement;
    if (this.activeTrigger === document.body) {
      this.activeTrigger = summaryElement;
    }

    if (this.activeTrigger && this.activeTrigger.setAttribute) {
      this.activeTrigger.setAttribute('aria-expanded', 'true');
    }

    if (detailsElement.hasAttribute('open')) {
      this.setRefreshState(this.dataset.cartPending === 'true');
      this.focusDrawer();
      return;
    }

    this.log('drawer open requested');
    this.drawer.openMenuDrawer(summaryElement);
    this.setRefreshState(this.dataset.cartPending === 'true');
    this.focusDrawer();
  }

  focusDrawer() {
    window.setTimeout(() => {
      const focusTargets = [
        this.querySelector('.mini-cart__footer button[name="checkout"]:not([disabled])'),
        this.querySelector('.buddy-cart__empty-cta'),
        this.querySelector('drawer-close-button'),
        this.querySelector('[tabindex], button, a[href], input, select, textarea')
      ];
      const focusTarget = focusTargets.find((target) => this.isVisibleFocusableElement(target));
      if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }
    }, 450);
  }

  isVisibleFocusableElement(element) {
    if (!element || typeof element.focus !== 'function') return false;
    if (element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true') return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    return element.getClientRects().length > 0;
  }

  onDrawerClose() {
    this.cancelPendingOpen();

    const summaryElement = this.drawer && this.drawer.querySelector('summary');

    if (summaryElement) {
      summaryElement.setAttribute('aria-expanded', 'false');
    }

    if (this.activeTrigger && this.activeTrigger.setAttribute) {
      this.activeTrigger.setAttribute('aria-expanded', 'false');
    }

    if (this.activeTrigger && typeof this.activeTrigger.focus === 'function') {
      this.activeTrigger.focus({ preventScroll: true });
    }

    this.activeTrigger = null;
  }

  handleCartAddSuccess(parsedState, trigger = null) {
    const renderRequestId = ++this.renderRequestId;

    this.log('add-to-cart succeeded');
    this.prepareForOpen();
    this.setRefreshState(true);

    // Open as soon as Shopify confirms the add succeeded, before doing any
    // synchronous section parsing/replacement. Fresh cart HTML is applied below;
    // focus is delayed by open(), so keyboard focus lands in the updated drawer.
    if (renderRequestId === this.renderRequestId) {
      this.open(trigger);
    }

    try {
      this.renderSections(parsedState);
      this.setRefreshState(false);
      document.dispatchEvent(new CustomEvent('cartdrawer:updated', { detail: { cart: parsedState } }));
    }
    catch (error) {
      this.log('cart drawer section render failed', error);
      this.refreshFromServer(renderRequestId)
        .catch((refreshError) => {
          this.log('cart drawer fallback refresh failed', refreshError);
          this.setRefreshState(false, true);
        });
    }
  }

  renderContents(parsedState, trigger = null) {
    this.handleCartAddSuccess(parsedState, trigger);
  }

  renderSections(parsedState) {
    if (!parsedState || !parsedState.sections) {
      throw new Error('Missing cart sections in add-to-cart response.');
    }

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      const sectionHtml = parsedState.sections[section.id];

      if (!element || !sectionHtml) return;

      element.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });
  }

  refreshFromServer(renderRequestId = this.renderRequestId) {
    if (this.refreshPromise) return this.refreshPromise;

    this.log('cart drawer refresh started');
    this.setRefreshState(true);

    this.refreshPromise = fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error(`Cart drawer refresh failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (renderRequestId !== this.renderRequestId) return;

        this.innerHTML = this.getSectionInnerHTML(html, '.shopify-section');
        this.setRefreshState(false);
        this.log('cart drawer refresh completed');
        document.dispatchEvent(new CustomEvent('cartdrawer:updated'));
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  setRefreshState(isRefreshing, isError = false) {
    this.dataset.cartPending = isRefreshing ? 'true' : 'false';
    this.dataset.cartDrawerState = isError ? 'error' : (isRefreshing ? 'refreshing' : (this.drawer?.querySelector('details[open]') ? 'open' : 'closed'));
    this.toggleAttribute('aria-busy', isRefreshing);
  }

  log(...args) {
    if (this.debug) console.debug('[Buddy cart drawer]', ...args);
  }

  prepareForOpen() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    document.body.classList.add('daily-dog-cart-pending');
  }

  cancelPendingOpen() {
    document.body.classList.remove('daily-dog-cart-pending');
    this.setRefreshState(false);
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }
}

customElements.define('mini-cart', MiniCart);
